import { HiringGoalOverview } from 'api-clients/monolith/models/HiringGoalOverview';
import { PaginateJsonSchema } from 'api-clients/monolith/models/PaginateJsonSchema';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import Applicant from '@/assets/icons/applicant.svg';
import IconRight from '@/assets/icons/icon-right.svg';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

import { HiringGoalPill } from './hiring-goal-pill';

interface HiringGoalDetailsListProps {
  hiringGoals: HiringGoalOverview[];
  goalStatus: string;
  pagination?: PaginateJsonSchema;
  setPage?: (page: number) => void;
  onClose: () => void;
}

function HiringGoalDetailsList({
  hiringGoals,
  goalStatus,
  pagination,
  setPage,
  onClose,
}: HiringGoalDetailsListProps) {
  const { hiringGoal } = useOpeningContext();
  const [_searchParams, setSearchParams] = useSearchParams();
  const getProgress = (expected: number | null, actual: number | null) => {
    if (!actual || !expected) return 0;

    return (actual / expected) * 100;
  };

  const handleViewClick = (externalId: string) => {
    onClose();
    setSearchParams(prevParams => {
      prevParams.set('hiring_goal_id', externalId);
      return prevParams;
    });
  };

  const handleLoadMore = (nextPage: number | null) => {
    if (setPage && nextPage !== null) {
      setPage(nextPage);
    }
  };

  return (
    <>
      {hiringGoals.map(goal => (
        <>
          <div className="flex flex-col items-end gap-4 self-stretch rounded-lg bg-white p-4 shadow-sm">
            <div className="flex min-h-[36px] items-center gap-4 self-stretch">
              <div className="flex grow items-center gap-2">
                <Typography variant="large2">
                  <FormattedMessage
                    defaultMessage="{actual} of {expected}"
                    values={{
                      actual: goal.hire_stats.actual,
                      expected: goal.hire_stats.expected,
                    }}
                  />
                </Typography>
              </div>
              <div className="flex w-[120px] justify-end">
                {goalStatus !== 'scheduled' &&
                  (hiringGoal?.external_id !== goal.external_id ? (
                    <Button
                      variant="secondary"
                      onClick={() => handleViewClick(goal.external_id)}
                    >
                      <FormattedMessage defaultMessage="View" />
                      <img src={IconRight} alt="IconRight" />
                    </Button>
                  ) : (
                    <HiringGoalPill />
                  ))}{' '}
              </div>
            </div>
            <Progress
              value={getProgress(
                goal.hire_stats.expected,
                goal.hire_stats.actual,
              )}
              className="bg-fx-primary"
            />
            <div className="flex items-start justify-between self-stretch">
              <div className="flex flex-col items-start gap-1">
                <Typography variant="caption">
                  <FormattedMessage defaultMessage="Duration" />
                </Typography>
                <Typography variant="subtitle1">
                  {formatShortDate(goal.start_date)} -{' '}
                  {formatShortDate(goal.hire_by)}
                </Typography>
              </div>
              <div className="flex flex-col items-start gap-1 self-stretch">
                <Typography variant="caption">
                  <FormattedMessage defaultMessage="Applicants" />
                </Typography>
                <div className="flex h-6 items-center justify-end gap-4">
                  <div className="flex h-4 items-center gap-2">
                    <img src={Applicant} alt="Applicant" />
                    <Typography variant="small1">
                      {goal.applied_count ?? 0}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ))}

      {pagination && pagination.current_page < pagination.total_pages && (
        <div className="flex items-center justify-end gap-1 self-stretch p-1.5">
          <Button
            variant="ghost"
            onClick={() => handleLoadMore(pagination.next_page)}
          >
            <Typography variant="body2">
              <FormattedMessage defaultMessage="More goals" />
            </Typography>
          </Button>
        </div>
      )}
    </>
  );
}

export { HiringGoalDetailsList };
